import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sliders, AlertTriangle, ShieldCheck, Eye, Save, Smartphone, Wrench } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input, Textarea } from '../../components/ui/Input.js';
import { JsonViewer } from '../../components/ui/JsonViewer.js';
import { ConfirmDialog } from '../../components/ui/Modal.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { ErrorState } from '../../components/ui/EmptyState.js';
import { PermissionGuard } from '../../components/security/PermissionGuard.js';
import { useToast } from '../../context/ToastContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchAppConfig, updateAppConfig } from '../../api/config.js';
import { formatDate } from '../../lib/utils.js';

export function RemoteConfigPage() {
  const queryClient = useQueryClient();
  const { success, error: toastError } = useToast();
  const { t } = useI18n();

  const { data: config, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-config'],
    queryFn: fetchAppConfig,
  });

  // Local form state
  const [minVersion, setMinVersion] = useState('');
  const [latestVersion, setLatestVersion] = useState('');
  const [forceUpdate, setForceUpdate] = useState(false);
  const [androidUrl, setAndroidUrl] = useState('');
  const [iosUrl, setIosUrl] = useState('');
  const [changelog, setChangelog] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    if (config) {
      setMinVersion(config.min_supported_version || '1.0.0');
      setLatestVersion(config.latest_version || '1.2.0');
      setForceUpdate(Boolean(config.force_update));
      setAndroidUrl(config.android_url || '');
      setIosUrl(config.ios_url || '');
      setChangelog(config.changelog || '');
      setMaintenanceMode(Boolean(config.maintenance_mode));
      setMaintenanceMessage(config.maintenance_message || '');
    }
  }, [config]);

  const updateMutation = useMutation({
    mutationFn: () =>
      updateAppConfig({
        min_supported_version: minVersion,
        latest_version: latestVersion,
        force_update: forceUpdate,
        android_url: androidUrl || null,
        ios_url: iosUrl || null,
        changelog: changelog || null,
        maintenance_mode: maintenanceMode,
        maintenance_message: maintenanceMessage || null,
      }),
    onSuccess: () => {
      success(t('config.saveSuccess'));
      setIsConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin-config'] });
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to update remote config', err.requestId);
    },
  });

  if (isLoading) return <CardSkeleton />;
  if (error || !config) return <ErrorState title={t('config.loadError')} message={(error as any).message} onRetry={() => refetch()} />;

  const previewPayload = {
    min_supported_version: minVersion,
    latest_version: latestVersion,
    force_update: forceUpdate,
    android_url: androidUrl || null,
    ios_url: iosUrl || null,
    changelog: changelog || null,
    maintenance_mode: maintenanceMode,
    maintenance_message: maintenanceMessage || null,
  };

  const isDangerousChange = maintenanceMode || forceUpdate;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Sliders className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            {t('config.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('config.description')} (Updated: {formatDate(config.updated_at)})
          </p>
        </div>

        <PermissionGuard permission="config:write">
          <Button
            variant={isDangerousChange ? 'danger' : 'primary'}
            onClick={() => setIsConfirmOpen(true)}
            className="gap-2"
          >
            <Save className="w-4 h-4" />
            {t('config.saveConfigBtn')}
          </Button>
        </PermissionGuard>
      </div>

      {/* Main Grid: Form on Left, Live Preview on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Controls */}
        <div className="lg:col-span-2 space-y-6">
          {/* App Versioning Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-indigo-500" />
                {t('config.minVersionTitle')}
              </CardTitle>
              <CardDescription>
                {t('config.minVersionDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-slate-300">
                    {t('config.minVersionTitle')}
                  </label>
                  <Input
                    value={minVersion}
                    onChange={(e) => setMinVersion(e.target.value)}
                    placeholder="e.g. 1.0.0"
                    className="font-mono text-xs"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-slate-300">
                    {t('config.latestVersionTitle')}
                  </label>
                  <Input
                    value={latestVersion}
                    onChange={(e) => setLatestVersion(e.target.value)}
                    placeholder="e.g. 1.2.0"
                    className="font-mono text-xs"
                  />
                </div>
              </div>

              {/* Force Update Danger Toggle */}
              <div className="p-3.5 rounded-xl border border-amber-500/20 bg-amber-500/5 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    {t('config.forceUpdateTitle')}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {t('config.forceUpdateDesc')}
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={forceUpdate}
                  onChange={(e) => setForceUpdate(e.target.checked)}
                  className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-slate-300">
                    {t('config.androidUrl')}
                  </label>
                  <Input
                    value={androidUrl}
                    onChange={(e) => setAndroidUrl(e.target.value)}
                    placeholder="https://play.google.com/store/apps/details?id=..."
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-slate-300">
                    {t('config.iosUrl')}
                  </label>
                  <Input
                    value={iosUrl}
                    onChange={(e) => setIosUrl(e.target.value)}
                    placeholder="https://apps.apple.com/app/hisob/id..."
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-slate-300">
                  {t('config.changelogTitle')}
                </label>
                <Textarea
                  rows={3}
                  value={changelog}
                  onChange={(e) => setChangelog(e.target.value)}
                  placeholder="Describe key improvements, fixes, or database migrations in this release..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Maintenance Mode Card */}
          <Card className={maintenanceMode ? 'border-rose-500/50 bg-rose-500/5' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-rose-500" />
                {t('config.maintenanceTitle')}
              </CardTitle>
              <CardDescription>
                {t('config.maintenanceDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-xs">
              <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                    {t('config.maintenanceTitle')}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {t('config.maintenanceDesc')}
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={maintenanceMode}
                  onChange={(e) => setMaintenanceMode(e.target.checked)}
                  className="w-5 h-5 rounded text-rose-600 focus:ring-rose-500"
                />
              </div>

              {maintenanceMode && (
                <div className="space-y-1">
                  <label className="font-semibold text-slate-700 dark:text-slate-300">
                    {t('config.maintenanceMessageTitle')}
                  </label>
                  <Input
                    value={maintenanceMessage}
                    onChange={(e) => setMaintenanceMessage(e.target.value)}
                    placeholder="Tizimda profilaktika ishlari olib borilmoqda. Tez orada qaytamiz."
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Live Edge Payload Preview */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-indigo-500" />
                Live Edge KV Payload
              </CardTitle>
              <CardDescription>
                Simulated response for GET /api/v1/config/app
              </CardDescription>
            </CardHeader>
            <CardContent>
              <JsonViewer data={previewPayload} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => updateMutation.mutate()}
        title={t('config.confirmModalTitle')}
        message={t('config.confirmModalMessage')}
        confirmText={t('config.saveConfigBtn')}
        variant={isDangerousChange ? 'danger' : 'primary'}
        requireVerificationText={isDangerousChange ? 'UPDATE' : undefined}
        isLoading={updateMutation.isPending}
      />
    </div>
  );
}
